import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/600.css';
import '@fontsource/ibm-plex-sans-arabic/700.css';
import '@mantine/core/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import '@mantine/charts/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import 'dayjs/locale/ar';
import 'mantine-datatable/styles.layer.css';
import './ui/global.css';
import { CacheProvider } from '@emotion/react';
import { DirectionProvider, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ApiError } from './api/client';
import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { ToastProvider } from './components/Toast';
import { initFavicon } from './lib/favicon';
import { getUiLanguage } from './lib/i18n';
import { ltrCache, rtlCache, theme } from './theme';
import { platformTheme } from './ui/theme';

initFavicon();

const language = getUiLanguage();
const dir = language === 'ar' ? 'rtl' : 'ltr';
document.documentElement.lang = language;
document.documentElement.dir = dir;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

// Mantine draws the console; the MUI theme stays for the shared form renderer (builder preview).
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <DirectionProvider initialDirection={dir} detectDirection={false}>
      <MantineProvider theme={platformTheme} defaultColorScheme="light" forceColorScheme="light">
        <DatesProvider settings={{ locale: language, firstDayOfWeek: 6, weekendDays: [5] }}>
        <CacheProvider value={language === 'ar' ? rtlCache : ltrCache}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <ToastProvider>
                  <AuthProvider>
                    <App />
                  </AuthProvider>
                </ToastProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </ThemeProvider>
        </CacheProvider>
        </DatesProvider>
      </MantineProvider>
    </DirectionProvider>
  </StrictMode>,
);
